/**
 * Domain Entity: Folder
 * Represents a practice folder with business logic
 */
export class Folder {
  private constructor(
    private readonly _id: string,
    private readonly _userId: string,
    private readonly _name: string,
    private readonly _description: string,
    private readonly _color: string,
    private readonly _isPublic: boolean,
    private readonly _practiceCount: number,
    private readonly _createdAt: Date = new Date(),
    private readonly _updatedAt: Date = new Date()
  ) {}

  static create(
    id: string,
    userId: string,
    name: string,
    description: string,
    color: string = '#3B82F6',
    isPublic: boolean = false
  ): Folder {
    if (!id || !userId || !name) {
      throw new Error('Folder ID, User ID, and name are required');
    }

    if (name.trim().length === 0) {
      throw new Error('Folder name cannot be empty');
    }

    if (name.length > 100) {
      throw new Error('Folder name cannot exceed 100 characters');
    }

    return new Folder(id, userId, name, description, color, isPublic, 0);
  }

  static fromData(data: {
    id: string;
    user_id: string;
    name: string;
    description: string;
    color: string;
    is_public: boolean;
    practice_count?: number;
    created_at?: string;
    updated_at?: string;
  }): Folder {
    return new Folder(
      data.id,
      data.user_id,
      data.name,
      data.description,
      data.color,
      data.is_public,
      data.practice_count || 0,
      data.created_at ? new Date(data.created_at) : new Date(),
      data.updated_at ? new Date(data.updated_at) : new Date()
    );
  }

  get id(): string {
    return this._id;
  }

  get userId(): string {
    return this._userId;
  }

  get name(): string {
    return this._name;
  }

  get description(): string {
    return this._description;
  }

  get color(): string {
    return this._color;
  }

  get isPublic(): boolean {
    return this._isPublic;
  }

  get practiceCount(): number {
    return this._practiceCount;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  isEmpty(): boolean {
    return this._practiceCount === 0;
  }

  isOwner(userId: string): boolean {
    return this._userId === userId;
  }

  canBeAccessedBy(userId: string): boolean {
    return this.isOwner(userId) || this._isPublic;
  }

  toJSON() {
    return {
      id: this._id,
      user_id: this._userId,
      name: this._name,
      description: this._description,
      color: this._color,
      is_public: this._isPublic,
      practice_count: this._practiceCount,
      created_at: this._createdAt.toISOString(),
      updated_at: this._updatedAt.toISOString()
    };
  }
}
